// nav-section — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
    background: var(--run-nav-section-background, var(--run-color-surface-default));
    color: var(--run-nav-section-color, var(--run-color-text-primary));
  }

  [part="trigger"] {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-block: var(--run-nav-section-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-nav-section-padding-inline, var(--run-space-semantic-xs));
    gap: var(--run-nav-section-gap, var(--run-space-semantic-xs));
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: start;
    box-sizing: border-box;
  }

  [part="trigger"]:hover {
    background: var(--run-nav-section-trigger-bg-hover, var(--run-color-state-hover-overlay));
  }

  [part="trigger"]:focus-visible {
    outline: var(--run-nav-section-focus-ring-width, 2px) solid var(--run-nav-section-focus-ring-color, var(--run-color-focus-ring));
    outline-offset: -2px;
  }

  [part="label"] {
    flex: 1;
    color: var(--run-nav-section-label-color, var(--run-color-text-secondary));
    font-size: var(--run-nav-section-label-font-size, var(--run-font-size-sm));
    font-weight: var(--run-nav-section-label-font-weight, var(--run-font-semantic-weight-medium));
  }

  :host([data-level="0"]) [part="label"] {
    font-size: var(--run-nav-section-label-size, 0.6875rem);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  [part="chevron"] {
    color: var(--run-nav-section-chevron-color, var(--run-color-text-secondary));
    transition:
      transform var(--run-nav-section-motion-duration, var(--run-motion-semantic-micro-duration))
      var(--run-nav-section-motion-easing, var(--run-motion-semantic-micro-easing));
    flex-shrink: 0;
  }

  :host([open]) [part="chevron"] {
    transform: rotate(90deg);
  }

  [part="panel"] {
    overflow: hidden;
    height: 0;
    transition:
      height var(--run-nav-section-motion-duration, var(--run-motion-semantic-micro-duration))
      var(--run-nav-section-motion-easing, var(--run-motion-semantic-micro-easing));
  }

  :host([open]) [part="panel"] {
    height: var(--run-nav-section-measured-height, auto);
  }

  [part="region"] {
    padding-inline-start: calc(
      var(--run-nav-section-indent, 0.5rem) * var(--run-nav-section-level, 0)
    );
    border-top: var(--run-nav-section-divider-width, 1px)
      solid var(--run-nav-section-divider-color, var(--run-color-border-default));
  }
`;

export function render(host) {
  const { label, open, level, _regionId } = host;

  const panelId = _regionId || 'run-nav-section-panel';
  const ariaExpanded = open ? 'true' : 'false';
  const levelClass = level === 0 ? 'label--level-0' : 'label--level-n';

  // Patch in place when the shadow structure already exists so that stable DOM
  // element references (captured by tests or listeners) are not invalidated.
  // Only do a full innerHTML reset on the first render.
  const existingTrigger = host.shadowRoot.querySelector('.trigger');

  if (!existingTrigger) {
    // First render — build the full structure.
    host.shadowRoot.innerHTML = `
    <button
      class="trigger"
      part="trigger"
      aria-expanded="${ariaExpanded}"
      aria-controls="${panelId}"
    >
      <run-text part="label" class="${levelClass}">${label ?? ''}</run-text>
      <run-icon part="chevron" name="chevron-right" aria-hidden="true"></run-icon>
    </button>
    <div part="panel">
      <run-stack
        id="${panelId}"
        part="region"
        role="region"
        aria-label="${label ?? ''}"
      >
        <slot></slot>
      </run-stack>
    </div>
  `;
  } else {
    // Subsequent renders — update attributes in place to preserve DOM identity.
    existingTrigger.setAttribute('aria-expanded', ariaExpanded);
    existingTrigger.setAttribute('aria-controls', panelId);

    const labelEl = existingTrigger.querySelector('[part="label"]');
    if (labelEl) {
      labelEl.className = levelClass;
      labelEl.textContent = label ?? '';
    }

    const region = host.shadowRoot.querySelector('[part="region"]');
    if (region) {
      region.setAttribute('id', panelId);
      region.setAttribute('aria-label', label ?? '');
    }
  }
}